import { ref, readonly } from 'vue'
import { getFlag } from '@/utils/feature-flags'

/**
 * Vue composable that resolves a feature-flag value and exposes a `ready`
 * flag alongside it, so templates can gate flag-driven content (e.g. flag-
 * driven copy) behind a "loaded" state instead of rendering the hardcoded
 * default on first paint and then flipping to the real value once the flag
 * resolves from the CDN.
 *
 * Usage:
 *   const { value: trialLength, ready } = useFlagValue('trial-length', 30)
 *
 * @param {string} key - Flag key
 * @param {*} defaultValue - Fallback value, also the value held until ready
 * @returns {{ value: import('vue').Ref, ready: import('vue').Ref<boolean> }}
 */
export function useFlagValue(key, defaultValue) {
    const value = ref(defaultValue)
    const ready = ref(false)

    // Test / dev override — resolve immediately, matching getFlag's own
    // override handling, so callers never see a transient "not ready" state
    // for a value that's actually already known. hasOwnProperty guards the
    // dynamic-key lookup against inherited properties (e.g. a `key` of
    // "toString" or "__proto__" resolving through the prototype chain
    // instead of being treated as "no override set").
    const overrides = globalThis.window?.__ZENMANAGE_FLAGS__
    if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
        value.value = Reflect.get(overrides, key)
        ready.value = true
        return { value: readonly(value), ready: readonly(ready) }
    }

    // getFlag() already catches its own failures and resolves to
    // defaultValue, so there's no rejection path to handle here — `void`
    // marks the chain as intentionally not awaited by the caller.
    void getFlag(key, defaultValue)
        .then((resolved) => {
            value.value = resolved
        })
        .finally(() => {
            ready.value = true
        })

    return { value: readonly(value), ready: readonly(ready) }
}
